import React, { useState, useCallback, useEffect } from "react";
import { Container, Button } from "semantic-ui-react";
import { Link } from "react-router-dom";
import SmartDataTable from "react-smart-data-table";
import { useSelector } from "react-redux";

import firebase from "../../../app/config/firebase";
const firestore = firebase.firestore();

export default function UsersPage(props) {
    const [filterValue, setFilterValue] = useState("");
    const [userlist, setUserlist] = useState([]);
    const [emailLookup, setEmailLookup] = useState("");
    const [uidLookup, setUidLookup] = useState("");
    const [lookupUser, setLookupUser] = useState(null);
    const [lookupStatus, setLookupStatus] = useState("");

    const { currentUserProfile } = useSelector((state) => state.profile);

    useEffect(() => {
        let unsubscribe = () => {};
        let unsubscribeExternal = () => {};

        unsubscribe = firestore
            .collection("users")
            .where("isConsultant", "==", true)
            .onSnapshot((snapshot) => {
                let list = [];

                snapshot.docs.forEach((doc) => {
                    if (doc.exists) {
                        let appObj = { ...doc.data(), id: doc.id };
                        appObj.accountStatus = appObj.isDisabled ? "Disabled" : "Active";
                        list.push(appObj);
                    }
                });
                unsubscribeExternal = firestore
                    .collection("users")
                    .where("isExternal", "==", true)
                    .onSnapshot((snapshot) => {
                        snapshot.docs.forEach((doc) => {
                            if (doc.exists) {
                                let appObj = { ...doc.data(), id: doc.id };
                                appObj.accountStatus = appObj.isDisabled ? "Disabled" : "Active";
                                list.push(appObj);
                            }
                        });

                        setUserlist(list);
                    });
            });

        return () => {
            unsubscribe();
            unsubscribeExternal();
        };
    }, []);

    if (currentUserProfile?.isLoaded === true && currentUserProfile?.isEmpty === true) {
        props.history.push("/");
    }

    if (currentUserProfile?.isSuper === false) {
        props.history.push("/");
    }

    const updateFilter = useCallback((value) => {
        setFilterValue(value);
    }, []);

    function handleOnChange({ target: { name, value } }) {
        updateFilter(value);
    }

    function onRowClick(event, { rowData, rowIndex, tableData }) {
        const { fullName, name, id } = rowData;
        let value = fullName || name || id;

        if (!value) {
            const [key] = Object.keys(rowData);
            value = `${key}: ${rowData[key]}`;
        }

        props.history.push("/admin/user/" + value);
    }

    const headers = {
        userUid: {
            invisible: true,
        },
    };

    const orderedHeaders = currentUserProfile?.isSysAdmin
        ? ["displayName", "email", "accountStatus", "userType", "isConsultant", "isSuper", "isExternal", "isCurrentAgent", "division"]
        : ["displayName", "email", "userType", "isConsultant", "isSuper", "isExternal", "isCurrentAgent", "division"];

    const tableData = (() => {
        if (!lookupUser) return userlist;
        const exists = userlist.some((user) => user.id === lookupUser.id);
        if (exists) return userlist;
        return [lookupUser, ...userlist];
    })();

    const canLookup = currentUserProfile?.isAdmin || currentUserProfile?.isSysAdmin;

    async function handleLookup() {
        if (!canLookup) {
            setLookupUser(null);
            setLookupStatus("unauthorized");
            return;
        }
        if (!emailLookup) {
            setLookupUser(null);
            setLookupStatus("empty");
            return;
        }
        try {
            const normalized = emailLookup.trim().toLowerCase();
            const localMatch = userlist.find((user) => {
                const email = (user.email || "").trim().toLowerCase();
                return email && email === normalized;
            });
            if (localMatch) {
                setLookupUser(localMatch);
                setLookupStatus("found_local");
                setFilterValue(emailLookup.trim());
                return;
            }

            let res = await firestore.collection("users").where("email", "==", emailLookup.trim()).get();
            if (res.empty && normalized && normalized !== emailLookup.trim()) {
                res = await firestore.collection("users").where("email", "==", normalized).get();
            }
            if (!res.empty) {
                const doc = res.docs[0];
                const appObj = { ...doc.data(), id: doc.id };
                appObj.accountStatus = appObj.isDisabled ? "Disabled" : "Active";
                appObj.lookupTag = "Lookup";
                setLookupUser(appObj);
                setLookupStatus("found_remote");
                setFilterValue(emailLookup.trim());
            } else {
                setLookupUser(null);
                setLookupStatus("not_found");
            }
        } catch (error) {
            console.log("[admin users] lookup failed", error);
            setLookupUser(null);
            setLookupStatus("error");
        }
    }

    async function handleUidLookup() {
        if (!canLookup) {
            setLookupUser(null);
            setLookupStatus("unauthorized");
            return;
        }
        if (!uidLookup) {
            setLookupUser(null);
            setLookupStatus("empty");
            return;
        }
        try {
            const doc = await firestore.collection("users").doc(uidLookup.trim()).get();
            if (doc.exists) {
                const appObj = { ...doc.data(), id: doc.id };
                appObj.accountStatus = appObj.isDisabled ? "Disabled" : "Active";
                appObj.lookupTag = "Lookup";
                setLookupUser(appObj);
                setLookupStatus("found_remote");
                setFilterValue(uidLookup.trim());
            } else {
                setLookupUser(null);
                setLookupStatus("not_found");
            }
        } catch (error) {
            console.log("[admin users] uid lookup failed", error);
            setLookupUser(null);
            setLookupStatus("error");
        }
    }

    return (
        <Container>
            <div style={{ marginBottom: 20 }}>
                <Button as={Link} to={`/admin/user/0`}>
                    Add User
                </Button>
                <Button as={Link} to={`/admin/landing`}>
                    Back to Admin
                </Button>

                <div className="ui icon input">
                    <input type="text" name="filterValue" value={filterValue} placeholder="Filter results..." onChange={handleOnChange} />
                    <i className="search icon" />
                </div>
                {canLookup && (
                    <div className="ui action input" style={{ marginLeft: 10 }}>
                        <input
                            type="text"
                            name="emailLookup"
                            value={emailLookup}
                            placeholder="Find by email..."
                            onChange={(e) => setEmailLookup(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleLookup();
                            }}
                        />
                        <Button type="button" color="orange" onClick={handleLookup}>
                            Find
                        </Button>
                    </div>
                )}
                {canLookup && (
                    <div className="ui action input" style={{ marginLeft: 10 }}>
                        <input
                            type="text"
                            name="uidLookup"
                            value={uidLookup}
                            placeholder="Find by auth UID..."
                            onChange={(e) => setUidLookup(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleUidLookup();
                            }}
                        />
                        <Button type="button" color="orange" onClick={handleUidLookup}>
                            Find
                        </Button>
                    </div>
                )}
            </div>
            {lookupStatus === "not_found" && <div style={{ marginBottom: 10, color: "#b00020" }}>No user found for that email.</div>}
            {lookupStatus === "error" && <div style={{ marginBottom: 10, color: "#b00020" }}>Lookup failed. Please try again.</div>}
            {lookupStatus === "empty" && <div style={{ marginBottom: 10, color: "#b00020" }}>Enter an email or UID to search.</div>}
            {lookupStatus === "unauthorized" && <div style={{ marginBottom: 10, color: "#b00020" }}>Lookup requires admin access.</div>}
            {lookupStatus === "found_local" && <div style={{ marginBottom: 10, color: "#1b5e20" }}>Found in current list.</div>}
            {lookupStatus === "found_remote" && <div style={{ marginBottom: 10, color: "#1b5e20" }}>Found via lookup.</div>}

            <SmartDataTable
                data={tableData}
                name="users-table"
                className="ui compact selectable table celled"
                sortable
                perPage={20}
                onRowClick={onRowClick}
                headers={headers}
                orderedHeaders={orderedHeaders}
                hideUnordered={true}
                filterValue={filterValue}
            />
        </Container>
    );
}
